/**
 * fast.ts — Categoría "Comidas Rápidas" (10 productos combinados con recetas reales).
 *
 * REGLA CRÍTICA: Cada ingrediente referenciado aquí existe como producto real
 * en otra categoría (pan, car, lac, fv, salsa, gra). El costo se calcula
 * automáticamente desde las recetas.
 */
import { buildProduct } from "./_factory";
import { ALMUERZO_MODIFIERS, HAMBURGUESA_MODIFIERS } from "./_modifiers";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_fast",
  skuPrefix: "FAST",
  categoria: "Comidas Rápidas",
} as const;

export const cat_fast_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Hamburguesa Clásica",
    marca: "Casa",
    costo: 0,
    precio: 18000,
    stock: 30,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Hamburguesa clásica de res 150g, queso cheddar derretido, lechuga fresca, tomate, cebolla y salsas de la casa. Pan artesanal con sésamo tostado a la plancha.\n\nAcompaña con papas fritas por solo $4.000 adicional.",
    especificaciones: [
      { clave: "Carne", valor: "Res 150g" },
      { clave: "Tiempo prep", valor: "8 min" },
    ],
    imageKeywords: ["hamburger", "burger", "cheeseburger"],
    color: "bg-amber-600",
    shape: "circle",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pan Hamburguesa x6",
        cantidadCompra: 6,
        unidadCompra: "unidad",
        costoCompra: 5200,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 150,
        unidadUso: "g",
      },
      {
        nombre: "Queso Cheddar Lonchas 200g",
        cantidadCompra: 12,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Lechuga Crespa Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1800,
        cantidadUso: 0.1,
        unidadUso: "unidad",
      },
      {
        nombre: "Tomate Chonto 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2200,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Mayonesa Fruco 380g",
        cantidadCompra: 380,
        unidadCompra: "g",
        costoCompra: 7200,
        cantidadUso: 15,
        unidadUso: "g",
      },
      {
        nombre: "Kétchup Fruco 380g",
        cantidadCompra: 380,
        unidadCompra: "g",
        costoCompra: 6800,
        cantidadUso: 15,
        unidadUso: "g",
      },
    ],
    gruposModificadores: HAMBURGUESA_MODIFIERS,
  }),
  buildProduct({
    ...CAT,
    index: 10,
    nombre: "Almuerzo Ejecutivo",
    marca: "Casa",
    costo: 0,
    precio: 16000,
    stock: 50,
    stockMinimo: 10,
    stockMaximo: 100,
    unidad: "porción",
    ubicacionFisica: "Cocina, menú del día",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Menú completo del día: sopa, principio, proteína y bebida. Elige cada componente al pedir.\n\nIdeal para oficinas y almuerzos rápidos.",
    especificaciones: [
      { clave: "Tipo", valor: "Corriente / ejecutivo" },
      { clave: "Tiempo prep", valor: "12 min" },
    ],
    imageKeywords: ["lunch", "colombian-food", "executive-lunch"],
    color: "bg-emerald-600",
    shape: "circle",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [],
    gruposModificadores: ALMUERZO_MODIFIERS,
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Hot Dog Sencillo",
    marca: "Casa",
    costo: 0,
    precio: 9500,
    stock: 40,
    stockMinimo: 12,
    stockMaximo: 100,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Perro caliente con salchicha ranchera ahumada, pan suave tibio, salsas rosada, mostaza y kétchup. Clásico colombiano al instante.",
    especificaciones: [{ clave: "Tiempo prep", valor: "4 min" }],
    imageKeywords: ["hot-dog", "perro-caliente", "sausage-bun"],
    color: "bg-red-500",
    shape: "square",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pan Hot Dog x8",
        cantidadCompra: 8,
        unidadCompra: "unidad",
        costoCompra: 4800,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Salchicha Ranchera Zenú x8",
        cantidadCompra: 8,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Salsa Rosada Fruco 200g",
        cantidadCompra: 200,
        unidadCompra: "g",
        costoCompra: 5800,
        cantidadUso: 20,
        unidadUso: "g",
      },
      {
        nombre: "Mostaza Fruco 240g",
        cantidadCompra: 240,
        unidadCompra: "g",
        costoCompra: 5400,
        cantidadUso: 10,
        unidadUso: "g",
      },
      {
        nombre: "Kétchup Fruco 380g",
        cantidadCompra: 380,
        unidadCompra: "g",
        costoCompra: 6800,
        cantidadUso: 10,
        unidadUso: "g",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Perro Mixto Especial",
    marca: "Casa",
    costo: 0,
    precio: 13500,
    stock: 35,
    stockMinimo: 10,
    stockMaximo: 80,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Perro doble: salchicha ranchera + tocineta ahumada crujiente, queso cheddar fundido, salsas de la casa, piña caramelizada y cebolla dorada.",
    especificaciones: [{ clave: "Tiempo prep", valor: "6 min" }],
    imageKeywords: ["loaded-hotdog", "bacon-dog", "gourmet-hotdog"],
    color: "bg-red-700",
    shape: "square",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pan Hot Dog x8",
        cantidadCompra: 8,
        unidadCompra: "unidad",
        costoCompra: 4800,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Salchicha Ranchera Zenú x8",
        cantidadCompra: 8,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Tocineta Ahumada 250g",
        cantidadCompra: 250,
        unidadCompra: "g",
        costoCompra: 9800,
        cantidadUso: 25,
        unidadUso: "g",
      },
      {
        nombre: "Queso Cheddar Lonchas 200g",
        cantidadCompra: 12,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Salsa de Piña Fruco 200g",
        cantidadCompra: 200,
        unidadCompra: "g",
        costoCompra: 6200,
        cantidadUso: 15,
        unidadUso: "g",
      },
      {
        nombre: "Cebolla Cabezona 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2600,
        cantidadUso: 20,
        unidadUso: "g",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Sándwich de Pollo",
    marca: "Casa",
    costo: 0,
    precio: 15500,
    stock: 28,
    stockMinimo: 8,
    stockMaximo: 60,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Pechuga de pollo a la plancha con especias de la casa, lechuga, tomate, queso cheddar y mayonesa en pan blanco artesanal tostado.",
    especificaciones: [
      { clave: "Pollo", valor: "150g" },
      { clave: "Tiempo prep", valor: "7 min" },
    ],
    imageKeywords: ["chicken-sandwich", "sandwich-pollo", "club-sandwich"],
    color: "bg-amber-400",
    shape: "square",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pan Blanco Tajado Bimbo 680g",
        cantidadCompra: 22,
        unidadCompra: "unidad",
        costoCompra: 6500,
        cantidadUso: 2,
        unidadUso: "unidad",
      },
      {
        nombre: "Pechuga de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 14500,
        cantidadUso: 150,
        unidadUso: "g",
      },
      {
        nombre: "Lechuga Crespa Verde",
        cantidadCompra: 1,
        unidadCompra: "unidad",
        costoCompra: 1800,
        cantidadUso: 0.1,
        unidadUso: "unidad",
      },
      {
        nombre: "Tomate Chonto 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2200,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Queso Cheddar Lonchas 200g",
        cantidadCompra: 12,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Mayonesa Fruco 380g",
        cantidadCompra: 380,
        unidadCompra: "g",
        costoCompra: 7200,
        cantidadUso: 15,
        unidadUso: "g",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Hamburguesa Doble Bacon",
    marca: "Casa",
    costo: 0,
    precio: 24500,
    stock: 25,
    stockMinimo: 8,
    stockMaximo: 55,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Doble carne 250g, doble queso cheddar, tocineta crujiente, cebolla caramelizada y salsa BBQ de la casa. Para valientes.",
    especificaciones: [
      { clave: "Carne", valor: "Res 250g (2x125g)" },
      { clave: "Tiempo prep", valor: "10 min" },
    ],
    imageKeywords: ["double-burger", "bacon-cheeseburger", "big-burger"],
    color: "bg-amber-800",
    shape: "circle",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Pan Hamburguesa x6",
        cantidadCompra: 6,
        unidadCompra: "unidad",
        costoCompra: 5200,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 250,
        unidadUso: "g",
      },
      {
        nombre: "Queso Cheddar Lonchas 200g",
        cantidadCompra: 12,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 2,
        unidadUso: "unidad",
      },
      {
        nombre: "Tocineta Ahumada 250g",
        cantidadCompra: 250,
        unidadCompra: "g",
        costoCompra: 9800,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Cebolla Cabezona 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2600,
        cantidadUso: 30,
        unidadUso: "g",
      },
      {
        nombre: "Salsa BBQ Heinz 400g",
        cantidadCompra: 400,
        unidadCompra: "g",
        costoCompra: 9200,
        cantidadUso: 20,
        unidadUso: "g",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Arepa de Carne",
    marca: "Casa",
    costo: 0,
    precio: 11500,
    stock: 40,
    stockMinimo: 12,
    stockMaximo: 90,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Arepa blanca rellena de carne desmechada, queso mozzarella derretido y hogao casero. Servida caliente al momento.",
    especificaciones: [
      { clave: "Tipo", valor: "Arepa rellena" },
      { clave: "Tiempo prep", valor: "6 min" },
    ],
    imageKeywords: ["stuffed-arepa", "arepa-rellena", "venezuelan-food"],
    color: "bg-yellow-500",
    shape: "circle",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Harina de Maíz Precocida 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 5200,
        cantidadUso: 80,
        unidadUso: "g",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 100,
        unidadUso: "g",
      },
      {
        nombre: "Queso Mozzarella Colanta 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 11000,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Cebolla Cabezona 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2600,
        cantidadUso: 20,
        unidadUso: "g",
      },
      {
        nombre: "Tomate Chonto 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2200,
        cantidadUso: 25,
        unidadUso: "g",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Arepa de Huevo",
    marca: "Casa",
    costo: 0,
    precio: 5500,
    stock: 50,
    stockMinimo: 15,
    stockMaximo: 100,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Arepa frita rellena de huevo entero, crujiente por fuera, suave por dentro. Desayuno costeño al instante.",
    especificaciones: [
      { clave: "Tipo", valor: "Costeña frita" },
      { clave: "Tiempo prep", valor: "5 min" },
    ],
    imageKeywords: ["fried-arepa", "arepa-huevo", "colombian-breakfast"],
    color: "bg-yellow-400",
    shape: "circle",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Harina de Maíz Precocida 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 5200,
        cantidadUso: 90,
        unidadUso: "g",
      },
      {
        nombre: "Huevos AA Kikes x30",
        cantidadCompra: 30,
        unidadCompra: "unidad",
        costoCompra: 14500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Aceite de Girasol Girasoli 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 9800,
        cantidadUso: 30,
        unidadUso: "ml",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Empanada de Carne",
    marca: "Casa",
    costo: 0,
    precio: 3500,
    stock: 80,
    stockMinimo: 20,
    stockMaximo: 160,
    unidad: "unidad",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Empanada colombiana de maíz rellena de carne de res con papa y guiso casero. Frita al momento, acompañada de ají.",
    especificaciones: [
      { clave: "Tipo", valor: "Maíz frita" },
      { clave: "Tiempo prep", valor: "4 min" },
    ],
    imageKeywords: ["empanada", "meat-pie", "colombian-empanada"],
    color: "bg-yellow-600",
    shape: "diamond",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Harina de Maíz Precocida 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 5200,
        cantidadUso: 40,
        unidadUso: "g",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 35,
        unidadUso: "g",
      },
      {
        nombre: "Papa Pastusa 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 2400,
        cantidadUso: 25,
        unidadUso: "g",
      },
      {
        nombre: "Aceite de Girasol Girasoli 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 9800,
        cantidadUso: 15,
        unidadUso: "ml",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Burrito de Res",
    marca: "Casa",
    costo: 0,
    precio: 17500,
    stock: 28,
    stockMinimo: 8,
    stockMaximo: 60,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "Tortilla grande rellena de carne molida sazonada, frijoles, arroz, queso derretido y salsa rosada. Enrollado al momento.",
    especificaciones: [
      { clave: "Tipo", valor: "Burrito grande" },
      { clave: "Tiempo prep", valor: "8 min" },
    ],
    imageKeywords: ["burrito", "mexican-wrap", "beef-burrito"],
    color: "bg-orange-600",
    shape: "square",
    gastoMargen: "ambicioso",
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Tortilla de Trigo Tía Rosa x10",
        cantidadCompra: 10,
        unidadCompra: "unidad",
        costoCompra: 5500,
        cantidadUso: 1,
        unidadUso: "unidad",
      },
      {
        nombre: "Carne Molida de Res 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 19500,
        cantidadUso: 120,
        unidadUso: "g",
      },
      {
        nombre: "Fríjol Rojo 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 4200,
        cantidadUso: 50,
        unidadUso: "g",
      },
      {
        nombre: "Arroz Blanco Diana 500g",
        cantidadCompra: 500,
        unidadCompra: "g",
        costoCompra: 2100,
        cantidadUso: 50,
        unidadUso: "g",
      },
      {
        nombre: "Queso Cheddar Lonchas 200g",
        cantidadCompra: 12,
        unidadCompra: "unidad",
        costoCompra: 8500,
        cantidadUso: 2,
        unidadUso: "unidad",
      },
      {
        nombre: "Salsa Rosada Fruco 200g",
        cantidadCompra: 200,
        unidadCompra: "g",
        costoCompra: 5800,
        cantidadUso: 20,
        unidadUso: "g",
      },
    ],
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Alitas BBQ x6",
    marca: "Casa",
    costo: 0,
    precio: 21900,
    stock: 30,
    stockMinimo: 8,
    stockMaximo: 65,
    unidad: "porción",
    ubicacionFisica: "Cocina, preparación al momento",
    proveedor: "Producción propia",
    tiempoEntrega: "Inmediato",
    descripcionLarga:
      "6 alitas crujientes bañadas en salsa BBQ ahumada, apanado en harina con especias. Acompañadas con papas fritas y salsa rosada.",
    especificaciones: [
      { clave: "Cantidad", valor: "6 alitas" },
      { clave: "Tiempo prep", valor: "12 min" },
    ],
    imageKeywords: ["chicken-wings", "bbq-wings", "fried-wings"],
    color: "bg-red-900",
    shape: "diamond",
    gastoMargen: "ambicioso",
    destacado: true,
    esReceta: true,
    recetaRendimiento: 1,
    recetaIngredientes: [
      {
        nombre: "Alitas de Pollo 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 9500,
        cantidadUso: 400,
        unidadUso: "g",
      },
      {
        nombre: "Harina de Trigo Haz de Oros 1kg",
        cantidadCompra: 1000,
        unidadCompra: "g",
        costoCompra: 4500,
        cantidadUso: 60,
        unidadUso: "g",
      },
      {
        nombre: "Aceite de Girasol Girasoli 1L",
        cantidadCompra: 1000,
        unidadCompra: "ml",
        costoCompra: 9800,
        cantidadUso: 50,
        unidadUso: "ml",
      },
      {
        nombre: "Salsa BBQ Heinz 400g",
        cantidadCompra: 400,
        unidadCompra: "g",
        costoCompra: 9200,
        cantidadUso: 60,
        unidadUso: "g",
      },
    ],
  }),
];
